import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from './api';
import type { TodoInput, TodoUpdate, TodoWithUsers } from '@stromo/shared';
import { useUndoStore } from './undoStore';

export interface TodoProjectGroup {
  project_id: number;
  order_number: string;
  title: string;
  open_count: number;
  total_count: number;
  todos: TodoWithUsers[];
}

export type TodoStatusFilter = 'open' | 'completed' | 'all';

export interface TodoOverviewFilters {
  status: TodoStatusFilter;
  q?: string;
  project_id?: number;
  /** Zahl = konkrete Sektion, 'none' = nur "Allgemein". */
  section_id?: number | 'none';
  created_by?: number;
  project_manager_id?: number;
  site_manager_id?: number;
}

/** Globale Übersicht über alle Projekte und ihre Todos. */
export function useAllTodos(filters: TodoOverviewFilters) {
  return useQuery({
    queryKey: ['todos', 'all', filters],
    queryFn: async () => {
      const params: Record<string, string | number> = { status: filters.status };
      if (filters.q?.trim()) params.q = filters.q.trim();
      if (filters.project_id) params.project_id = filters.project_id;
      if (filters.section_id) params.section_id = filters.section_id;
      if (filters.created_by) params.created_by = filters.created_by;
      if (filters.project_manager_id) params.project_manager_id = filters.project_manager_id;
      if (filters.site_manager_id) params.site_manager_id = filters.site_manager_id;
      const { data } = await api.get<{ groups: TodoProjectGroup[] }>('/todos', { params });
      return data.groups;
    },
    // Beim Tippen im Suchfeld die alte Liste stehen lassen statt zu flackern.
    placeholderData: (prev) => prev,
  });
}

/** Todos eines einzelnen Projekts — für die Card in der Detail-Ansicht. */
export function useProjectTodos(projectId: number | undefined) {
  return useQuery({
    queryKey: ['todos', 'project', projectId],
    queryFn: async () => {
      const { data } = await api.get<{ items: TodoWithUsers[] }>(`/projects/${projectId}/todos`);
      return data.items;
    },
    enabled: projectId !== undefined,
  });
}

/** Alle Todo-Listen neu laden — ein verschobenes Todo verschwindet aus
 *  dem alten Projekt und taucht im neuen auf, darum nicht nur `projectId`. */
function invalidateTodos(qc: ReturnType<typeof useQueryClient>) {
  qc.invalidateQueries({ queryKey: ['todos'] });
}

export function useCreateTodo(projectId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: TodoInput) => {
      const { data } = await api.post<TodoWithUsers>(`/projects/${projectId}/todos`, input);
      return data;
    },
    onSuccess: () => invalidateTodos(qc),
  });
}

export interface UpdateTodoVars {
  id: number;
  patch: TodoUpdate;
  /** Beim Verschieben in ein anderes Projekt: vorheriger Ort, damit der
   *  Undo-Toast das Todo zurückholen kann. */
  movedFrom?: { project_id: number; section_id: number | null };
}

/** Stand der Eingabefelder im Bearbeiten-Modus eines Todos. */
export interface TodoDraft {
  title: string;
  description: string;
  projectId: number;
  sectionId: number | null;
}

export const todoToDraft = (t: TodoWithUsers): TodoDraft => ({
  title: t.title,
  description: t.description ?? '',
  projectId: t.project_id,
  sectionId: t.section_id,
});

/** Baut aus dem Draft nur die geänderten Felder. `null` = nichts zu tun
 *  (oder Titel leer). Beim Projektwechsel wird die Sektion immer explizit
 *  mitgeschickt und der alte Ort für den Undo-Toast gemerkt. */
export function buildTodoUpdate(todo: TodoWithUsers, draft: TodoDraft): UpdateTodoVars | null {
  const title = draft.title.trim();
  if (!title) return null;
  const patch: TodoUpdate = {};
  if (title !== todo.title) patch.title = title;
  const description = draft.description.trim() || null;
  if (description !== todo.description) patch.description = description;
  const projectChanged = draft.projectId !== todo.project_id;
  if (projectChanged) patch.project_id = draft.projectId;
  if (projectChanged || draft.sectionId !== todo.section_id) patch.section_id = draft.sectionId;
  if (Object.keys(patch).length === 0) return null;
  return {
    id: todo.id,
    patch,
    ...(projectChanged && {
      movedFrom: { project_id: todo.project_id, section_id: todo.section_id },
    }),
  };
}

export function useUpdateTodo() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, patch }: UpdateTodoVars) => {
      const { data } = await api.patch<TodoWithUsers>(`/todos/${id}`, patch);
      return data;
    },
    onSuccess: (_data, { id, movedFrom }) => {
      invalidateTodos(qc);
      if (!movedFrom) return;
      useUndoStore.getState().show({
        label: 'Todo verschoben',
        onUndo: async () => {
          try {
            await api.patch(`/todos/${id}`, movedFrom);
            invalidateTodos(qc);
          } catch {
            /* silent */
          }
        },
      });
    },
  });
}

export function useToggleTodoComplete(projectId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, completed }: { id: number; completed: boolean }) => {
      const { data } = await api.post<TodoWithUsers>(`/todos/${id}/complete`, { completed });
      return data;
    },
    // Optimistic: setzt completed_at lokal sofort, damit der Haken nicht zappelt.
    onMutate: async ({ id, completed }) => {
      await qc.cancelQueries({ queryKey: ['todos', 'project', projectId] });
      const prev = qc.getQueryData<TodoWithUsers[]>(['todos', 'project', projectId]);
      if (prev) {
        qc.setQueryData<TodoWithUsers[]>(
          ['todos', 'project', projectId],
          prev.map((t) =>
            t.id === id
              ? {
                  ...t,
                  completed_at: completed ? new Date().toISOString() : null,
                  completed_by: completed ? t.completed_by : null,
                  completed_by_name: completed ? t.completed_by_name : null,
                  completed_by_abbr: completed ? t.completed_by_abbr : null,
                }
              : t,
          ),
        );
      }
      return { prev };
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.prev) qc.setQueryData(['todos', 'project', projectId], ctx.prev);
    },
    onSettled: () => invalidateTodos(qc),
  });
}

export function useDeleteTodo() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: number) => {
      await api.delete(`/todos/${id}`);
      return id;
    },
    onSuccess: (id) => {
      invalidateTodos(qc);
      useUndoStore.getState().show({
        label: 'Todo gelöscht',
        onUndo: async () => {
          try {
            await api.post(`/todos/${id}/restore`);
            invalidateTodos(qc);
          } catch {
            /* silent */
          }
        },
      });
    },
  });
}
